import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProfileIcon from '../assets/icons/User.svg';
import AdminIcon from '../assets/icons/Settings.svg';
import EditIcon from '../assets/icons/edit.svg';

const initialFormState = {
  id: null,
  username: '',
  name: '',
  password: '',
  admin_permission: '0',
  client_database: '',
};

const formatDateTime = (value) => {
  if (!value) return '--';
  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

function Settings() {
  const storedUsername = useMemo(
    () => localStorage.getItem('username') || sessionStorage.getItem('username') || '',
    []
  );

  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formData, setFormData] = useState(initialFormState);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const isAdmin = profile?.admin_permission === '1';

  const tabs = useMemo(() => {
    const base = [
      {
        id: 'profile',
        label: 'Profile',
        description: 'View personal account details',
        icon: ProfileIcon,
      },
    ];

    if (isAdmin) {
      base.push({
        id: 'admin',
        label: 'Admin',
        description: 'Manage platform user access',
        icon: AdminIcon,
      });
    }

    return base;
  }, [isAdmin]);

  useEffect(() => {
    if (!storedUsername) {
      setProfileLoading(false);
      setProfileError('No active user session found.');
      return;
    }

    setProfileLoading(true);
    setProfileError('');

    fetch(`/get_user_profile.php?username=${encodeURIComponent(storedUsername)}`)
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Unable to load profile information.');
        }
        setProfile(data.user);
      })
      .catch((error) => {
        setProfileError(error.message || 'Unable to load profile information.');
      })
      .finally(() => {
        setProfileLoading(false);
      });
  }, [storedUsername]);

  useEffect(() => {
    if (!isAdmin && activeTab === 'admin') {
      setActiveTab('profile');
    }
  }, [isAdmin, activeTab]);

  const fetchUsers = useCallback(() => {
    setUsersLoading(true);
    setUsersError('');

    fetch('/manage_users.php')
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Unable to fetch users.');
        }
        setUsers(Array.isArray(data.users) ? data.users : []);
      })
      .catch((error) => {
        setUsersError(error.message || 'Unable to fetch users.');
      })
      .finally(() => {
        setUsersLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, fetchUsers]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(timeout);
  }, [toast]);

  const openCreateModal = () => {
    setFormMode('create');
    setFormData(initialFormState);
    setFormError('');
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setFormMode('edit');
    setFormError('');
    setFormData({
      id: user.id,
      username: user.username || '',
      name: user.name || '',
      password: '',
      admin_permission: user.admin_permission === '1' ? '1' : '0',
      client_database: user.client_database || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
    setFormData(initialFormState);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormError('');

    const trimmedData = {
      ...formData,
      username: formData.username.trim(),
      name: formData.name.trim(),
      client_database: formData.client_database.trim(),
      password: formData.password.trim(),
    };

    if (trimmedData.username === '' || trimmedData.name === '' || trimmedData.client_database === '') {
      setFormError('Please complete all required fields.');
      return;
    }

    if (formMode === 'create' && trimmedData.password === '') {
      setFormError('A password is required when creating a user.');
      return;
    }

    const payload = {
      id: trimmedData.id,
      username: trimmedData.username,
      name: trimmedData.name,
      password: trimmedData.password,
      admin_permission: trimmedData.admin_permission,
      client_database: trimmedData.client_database,
    };

    setSaving(true);

    fetch('/manage_users.php', {
      method: formMode === 'create' ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Unable to save user changes.');
        }
        setToast({
          type: 'success',
          message: formMode === 'create' ? 'User created successfully.' : 'User updated successfully.',
        });
        closeModal();
        fetchUsers();
      })
      .catch((error) => {
        setFormError(error.message || 'Unable to save user changes.');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#151C2A] via-[#0C1017] to-[#080B10]" />
        <div className="absolute -top-28 -left-24 h-72 w-72 rounded-full bg-primary/30 blur-3xl opacity-60" />
        <div className="absolute -bottom-32 -right-10 h-80 w-80 rounded-full bg-indigo-500/30 blur-3xl opacity-60" />
      </div>

      <div className="relative space-y-10">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.45em] text-white/60">Dashboard</p>
            <h1 className="text-4xl font-semibold">Settings</h1>
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group relative overflow-hidden rounded-full border border-white/10 px-5 py-2 text-sm font-medium transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-white/15 text-white shadow-[0_0_25px_rgba(255,255,255,0.08)]'
                    : 'bg-white/5 text-white/70 hover:border-white/30 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-2">
                  <img src={tab.icon} alt="" className="h-4 w-4 opacity-80" />
                  <span>{tab.label}</span>
                </span>
                <AnimatePresence>
                  {activeTab === tab.id && (
                    <motion.span
                      layoutId="settings-tab-pill"
                      className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-primary/40 via-rose-500/40 to-cyan-400/40"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    />
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'profile' && (
            <motion.section
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
              className="space-y-6"
            >
              <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/30 backdrop-blur">
                      <img src={ProfileIcon} alt="Profile" className="h-8 w-8" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold">Personal Overview</h2>
                      <p className="text-sm text-white/60">
                        Review your account details and linked workspace access.
                      </p>
                    </div>
                  </div>
                  {profile && (
                    <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
                      <span className="rounded-full bg-white/10 px-4 py-1 uppercase tracking-widest">
                        {profile.admin_permission === '1' ? 'Administrator' : 'Standard User'}
                      </span>
                      <span className="rounded-full bg-white/10 px-4 py-1">
                        Joined {formatDateTime(profile.created_at)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {profileLoading && (
                    <div className="col-span-full flex items-center justify-center py-16 text-white/70">
                      Loading profile...
                    </div>
                  )}
                  {profileError && !profileLoading && (
                    <div className="col-span-full rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-200">
                      {profileError}
                    </div>
                  )}
                  {!profileLoading && !profileError && profile && (
                    <>
                      <motion.div
                        whileHover={{ y: -6 }}
                        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 transition-shadow duration-300"
                      >
                        <div className="text-xs uppercase tracking-widest text-white/60">Full Name</div>
                        <div className="mt-3 text-lg font-semibold">{profile.name}</div>
                        <span className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-white/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      </motion.div>

                      <motion.div
                        whileHover={{ y: -6 }}
                        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 transition-shadow duration-300"
                      >
                        <div className="text-xs uppercase tracking-widest text-white/60">Username</div>
                        <div className="mt-3 text-lg font-semibold">{profile.username}</div>
                        <span className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-primary/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      </motion.div>

                      <motion.div
                        whileHover={{ y: -6 }}
                        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 transition-shadow duration-300"
                      >
                        <div className="text-xs uppercase tracking-widest text-white/60">Assigned Schema</div>
                        <div className="mt-3 text-lg font-semibold">{profile.client_database || 'Not set'}</div>
                        <span className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-cyan-400/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      </motion.div>

                      <motion.div
                        whileHover={{ y: -6 }}
                        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 transition-shadow duration-300"
                      >
                        <div className="text-xs uppercase tracking-widest text-white/60">Role</div>
                        <div className="mt-3 text-lg font-semibold">
                          {profile.admin_permission === '1' ? 'Administrator' : 'User'}
                        </div>
                        <span className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-emerald-400/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      </motion.div>

                      <motion.div
                        whileHover={{ y: -6 }}
                        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 transition-shadow duration-300"
                      >
                        <div className="text-xs uppercase tracking-widest text-white/60">Member Since</div>
                        <div className="mt-3 text-lg font-semibold">{formatDateTime(profile.created_at)}</div>
                        <span className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-indigo-400/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      </motion.div>
                    </>
                  )}
                </div>
              </div>
            </motion.section>
          )}

          {activeTab === 'admin' && isAdmin && (
            <motion.section
              key="admin"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
              className="space-y-6"
            >
              <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/30 backdrop-blur">
                      <img src={AdminIcon} alt="Admin" className="h-8 w-8" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold">User Administration</h2>
                      <p className="text-sm text-white/60">
                        Maintain access and provisioning across the V Sense platform.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={openCreateModal}
                    className="group flex items-center gap-2 rounded-full border border-primary/60 bg-primary/80 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:bg-primary"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-lg leading-none">+</span>
                    New User
                  </button>
                </div>

                <div className="mt-8 space-y-4">
                  {usersLoading && (
                    <div className="flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 py-12 text-white/70">
                      Loading users...
                    </div>
                  )}
                  {usersError && !usersLoading && (
                    <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-6 text-rose-200">
                      {usersError}
                    </div>
                  )}
                  {!usersLoading && !usersError && (
                    <div className="overflow-hidden rounded-2xl border border-white/10">
                      <table className="min-w-full divide-y divide-white/10">
                        <thead className="bg-white/5 text-left text-xs font-semibold uppercase tracking-widest text-white/60">
                          <tr>
                            <th className="px-6 py-4">User</th>
                            <th className="px-6 py-4">Role</th>
                            <th className="px-6 py-4">Schema</th>
                            <th className="px-6 py-4">Created</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5 bg-white/5/50">
                          {users.length === 0 && (
                            <tr>
                              <td colSpan={5} className="px-6 py-10 text-center text-white/60">
                                No users found.
                              </td>
                            </tr>
                          )}
                          {users.map((user) => (
                            <tr
                              key={user.id}
                              className="transition-colors duration-200 hover:bg-white/10"
                            >
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  <span className="font-semibold text-white">{user.name || 'Unnamed user'}</span>
                                  <span className="text-xs text-white/60">{user.username}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span
                                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                                    user.admin_permission === '1'
                                      ? 'bg-emerald-500/20 text-emerald-300'
                                      : 'bg-white/10 text-white/70'
                                  }`}
                                >
                                  {user.admin_permission === '1' ? 'Admin' : 'User'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-white/80">{user.client_database || '—'}</td>
                              <td className="px-6 py-4 text-sm text-white/60">{formatDateTime(user.created_at)}</td>
                              <td className="px-6 py-4 text-right">
                                <button
                                  onClick={() => openEditModal(user)}
                                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-all duration-200 hover:border-primary/60 hover:bg-primary/70"
                                >
                                  <img src={EditIcon} alt="Edit" className="h-3.5 w-3.5" />
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25 }}
            className={`fixed bottom-6 right-6 z-50 rounded-xl px-5 py-3 text-sm font-medium shadow-2xl ${
              toast.type === 'success'
                ? 'bg-emerald-500/90 text-white'
                : 'bg-rose-500/90 text-white'
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#141A24] p-8 shadow-2xl"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold">
                    {formMode === 'create' ? 'Create New User' : 'Edit User'}
                  </h3>
                  <p className="text-sm text-white/60">
                    {formMode === 'create'
                      ? 'Provision a new user and assign their workspace schema.'
                      : 'Update access permissions and credentials for this user.'}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm uppercase tracking-wide text-white/60 transition hover:border-white/30 hover:text-white"
                >
                  Close
                </button>
              </div>

              {formError && (
                <div className="mb-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Full Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(event) => handleFormChange('name', event.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="VSense Admin"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(event) => handleFormChange('username', event.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="vsense"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">
                    {formMode === 'create' ? 'Password' : 'Password (leave blank to keep current)'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(event) => handleFormChange('password', event.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="••••••••"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/60">Role</label>
                    <select
                      value={formData.admin_permission}
                      onChange={(event) => handleFormChange('admin_permission', event.target.value)}
                      className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="0" className="bg-[#141A24] text-white">Standard User</option>
                      <option value="1" className="bg-[#141A24] text-white">Administrator</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/60">Client Schema</label>
                    <input
                      type="text"
                      value={formData.client_database}
                      onChange={(event) => handleFormChange('client_database', event.target.value)}
                      className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="v_sense"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-full border border-white/20 bg-white/5 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-white/70 transition hover:border-white/40 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-rose-500 px-6 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-lg transition hover:shadow-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : formMode === 'create' ? 'Create User' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Settings;
